import React, { useState, useEffect } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import { ErrorMessage } from "@hookform/error-message";

import { commonService } from "../../../services";
import { useAuthState } from "../../../contexts/context";
import { Role } from "../../../helpers";
import licensedStates from "../../../constant/licensedState";

const SelectField = ({
  field_id,
  field_label,
  field_options,
  default_value,
  validation,
}) => {
  const { register, errors, control, setValue, clearErrors, getValues } =
    useFormContext();
  const [selectValue, setSelectValue] = useState(() => {
    try {
      return default_value ? JSON.parse(default_value) : null;
    } catch (e) {
      return null;
    }
  });
  const userDetails = useAuthState();

  // Static (non-remote) selects render from local selectValue, so a
  // programmatic setValue() — e.g. the Encompass prefill — would update the
  // FORM state but not the visible dropdown: the user would see the default
  // while the engine priced the pulled value. Watch the form value and keep
  // the display in sync with it.
  const watchedValue = useWatch({ control, name: field_id });
  useEffect(() => {
    if (
      watchedValue &&
      typeof watchedValue === "object" &&
      watchedValue.value !== undefined &&
      (!selectValue || selectValue.value !== watchedValue.value)
    ) {
      setSelectValue(watchedValue);
    }
  }, [watchedValue]);
  // console.log("Default values >>> ", default_value);

  // load options using API call
  const loadOptions = (inputValue, callback) => {
    if (field_id == "property_state") {
      commonService.getAllStates().then((res) => {
        if (res.data.length > 0) {
          callback(res.data);
        }
      });
    }
    if (inputValue) {
      if (field_id == "zip_code_short" || field_id == "zcode_short") {
        commonService.getZipCodes(inputValue).then((res) => {
          if (res.data.length > 0) {
            callback(res.data);
          }
        });
      }
    } else callback([]);
  };

  const handleChange = async (value) => {
    // console.log(value);
    if (validationObj.source === "remote") {
      if (field_id == "zip_code_short") {
        setValue("zip_code_short", value);
        commonService.getStateByZip(value.value).then((res) => {
          if (res.data.length > 0) {
            setValue("property_state", res.data[0]);
            setSelectValue(value);
            clearErrors("zip_code_short");
            clearErrors("property_state");
          }
        });
      }
      if (field_id === "zcode_short") {
        setValue("zcode_short", value);
        commonService.getStateByZip(value.value).then((res) => {
          if (res.data.length > 0) {
            setValue("property_state", res.data[0]);
            setSelectValue(value);
            clearErrors("zcode_short");
            clearErrors("property_state");
          }
        });
      }
      if (field_id === "property_state") {
        setValue("property_state", value);
      }
    }

    if (field_id == "number_of_borrowers") {
      setValue("number_of_borrowers", value);
      if (value.value == 1) {
        setValue("co_borrower_credit_score_at_origination", null);
      }
    } else {
      setValue(field_id, value);
    }

    setSelectValue(value);
    clearErrors(field_id);
  };

  let validationObj = {};
  try { validationObj = validation ? JSON.parse(validation) : {}; } catch (e) { validationObj = {}; }
  let defaultValue = null;
  try { defaultValue = default_value ? JSON.parse(default_value) : null; } catch (e) { defaultValue = null; }
  // console.log(validationObj);

  // Theme-aware react-select styling. Uses CSS variables so the dropdowns flip
  // with light/dark mode (react-select accepts var(--...) values in inline styles).
  const bfSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "8px",
      minHeight: "42px",
      background: "var(--bf-surface)",
      borderColor: state.isFocused ? "var(--bf-brand-2)" : "var(--bf-field-border)",
      boxShadow: state.isFocused ? "0 0 0 3px var(--bf-focus)" : "none",
      color: "var(--bf-ink)",
    }),
    singleValue: (provided) => ({ ...provided, color: "var(--bf-ink)" }),
    input: (provided) => ({ ...provided, color: "var(--bf-ink)" }),
    placeholder: (provided) => ({ ...provided, color: "var(--bf-muted)" }),
    menu: (provided) => ({ ...provided, background: "var(--bf-surface)", zIndex: 20 }),
    menuList: (provided) => ({ ...provided, background: "var(--bf-surface)" }),
    option: (provided, state) => ({
      ...provided,
      background: state.isSelected
        ? "var(--bf-brand)"
        : state.isFocused
        ? "var(--bf-surface-2)"
        : "var(--bf-surface)",
      color: state.isSelected ? "#ffffff" : "var(--bf-ink)",
      cursor: "pointer",
    }),
    dropdownIndicator: (provided) => ({ ...provided, color: "var(--bf-muted)" }),
    indicatorSeparator: (provided) => ({ ...provided, backgroundColor: "var(--bf-field-border)" }),
  };

  const getSelectComponent = (field) => {
    if (validationObj.source == "remote") {
      return (
        <AsyncSelect
          cacheOptions
          {...register(field_id)}
          // defaultValue={field.defaultValue}
          isSearchable={true}
          loadOptions={loadOptions}
          onChange={handleChange}
          value={field.value}
          styles={bfSelectStyles}
        />
      );
    } else {
      return (
        <Select
          {...register(field_id)}
          options={mapFieldOptions(field_options)}
          onChange={handleChange}
          isSearchable={false}
          defaultValue={field.defaultValue}
          value={selectValue}
          styles={bfSelectStyles}
        />
      );
    }
  };

  const mapFieldOptions = (options) => {
    return options.map((option) => {
      return { label: option.option_label, value: option.option_value };
    });
  };
  const getLoanTermInfoMsg = () => {
    return "HELOC: Sum of Draw and Repay Terms; HELOAN: Final Term.";
  };
  const getDrawTermInfoMsg = () => {
    if (selectValue?.value === "36") {
      return "36-month Draws have the Best Rates and Compensation Options.";
    }
    return "Rates are higher / Correspondent Compensation is lower on longer draw terms.";
  };
  const getProductRequestedMsg = () => {
    if (selectValue?.value === "Blue") {
      return "Tri-Merge Hard Pull Credit Reports required";
    }
    return "";
  };
  return (
    <div className="mb-3">
      <label className="form-label">{field_label}</label>
      <Controller
        key={field_id}
        control={control}
        name={field_id}
        onChange={handleChange}
        defaultValue={defaultValue}
        // value={selectValue}
        render={({ field }) => getSelectComponent(field)}
        rules={{ required: "This field is required" }}
      />
      <small
        hidden={field_id != "2ndmortgage_loan_term"}
        className="form-text text-black"
      >
        {getLoanTermInfoMsg()}
      </small>
      <small
        hidden={field_id != "2ndmortgage_draw_term"}
        className="form-text text-black"
      >
        {getDrawTermInfoMsg()}
      </small>
      <small
        hidden={field_id != "product_requested"}
        className="form-text text-black"
      >
        {getProductRequestedMsg()}
      </small>
      {field_id === "property_state" && getValues("property_state") ? (
        <>
          {getValues("property_state")?.value === "TX" && (
            <small className="form-text text-black">
              Only HELOANs are available in TX. HELOCs are not available.
            </small>
          )}
          {!licensedStates.includes(getValues("property_state")?.value) && (
            <small className="form-text text-black">
              Not Licensed in {getValues("property_state")?.value}. Coming Soon!
            </small>
          )}
        </>
      ) : (
        ""
      )}
      <ErrorMessage
        errors={errors}
        name={field_id}
        render={({ message }) => <p className="fb-error-message">{message}</p>}
      />
    </div>
  );
};

export default SelectField;
